import React from "react";

/**
 * Pagination
 *
 * Drop-in pagination bar matching the Reva Jewellers blush/taupe design system.
 * Uses only classes already defined in common.css + the new rv-pagination block
 * you paste into common.css.
 *
 * Props — all come straight from usePagination():
 *   page, totalPages, pageSize, setPage, setPageSize,
 *   pageStart, pageEnd, totalItems
 *
 * Optional:
 *   pageSizeOptions  - array of numbers (default [10, 25, 50, 100])
 */
export default function Pagination({
  page,
  totalPages,
  pageSize,
  setPage,
  setPageSize,
  pageStart,
  pageEnd,
  totalItems,
  pageSizeOptions = [10, 25, 50, 100],
}) {
  if (totalItems === 0) return null;

  /* ── Build page number buttons with ellipsis ── */
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    // always show first, last, current ± 1, and ellipsis
    const left  = Math.max(2, page - 1);
    const right = Math.min(totalPages - 1, page + 1);

    pages.push(1);
    if (left > 2) pages.push("...");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push("...");
    pages.push(totalPages);
    return pages;
  };

  return (
    <div className="rv-pagination">

      {/* ── Left: showing X–Y of Z ── */}
      <div className="rv-pagination-info">
        Showing <strong>{pageStart}</strong>–<strong>{pageEnd}</strong> of{" "}
        <strong>{totalItems}</strong>
      </div>

      {/* ── Centre: page buttons ── */}
      <div className="rv-pagination-pages">
        {/* Prev */}
        <button
          className="rv-page-btn rv-page-btn--nav"
          onClick={() => setPage(page - 1)}
          disabled={page === 1}
          title="Previous page"
        >
          <ChevLeftIcon />
        </button>

        {getPageNumbers().map((p, i) =>
          p === "..." ? (
            <span key={`ellipsis-${i}`} className="rv-page-ellipsis">…</span>
          ) : (
            <button
              key={p}
              className={`rv-page-btn ${page === p ? "rv-page-btn--active" : ""}`}
              onClick={() => setPage(p)}
            >
              {p}
            </button>
          )
        )}

        {/* Next */}
        <button
          className="rv-page-btn rv-page-btn--nav"
          onClick={() => setPage(page + 1)}
          disabled={page === totalPages}
          title="Next page"
        >
          <ChevRightIcon />
        </button>
      </div>

      {/* ── Right: rows per page ── */}
      <div className="rv-pagination-size">
        <span className="rv-pagination-size-label">Rows per page</span>
        <div className="rv-select-wrap rv-pagination-select-wrap">
          <select
            className="rv-select rv-pagination-select"
            value={pageSize}
            onChange={e => setPageSize(Number(e.target.value))}
          >
            {pageSizeOptions.map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <span className="rv-select-arrow"><ChevDownIcon /></span>
        </div>
      </div>

    </div>
  );
}

/* ── Inline icons ── */
function ChevLeftIcon()  { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><polyline points="15 18 9 12 15 6"/></svg>; }
function ChevRightIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><polyline points="9 18 15 12 9 6"/></svg>; }
function ChevDownIcon()  { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><polyline points="6 9 12 15 18 9"/></svg>; }